import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { ApiLog, CountryOption, Operator } from '../types';

const EMPTY = { imsi: '', day: '', country: '', tadig: '' };

/** Turn a calendar day from the date input into the instants bounding it.
 *
 * Built from the browser's local midnight rather than sent as a bare date, so
 * "21 August" means the operator's day and not a UTC one shifted by the
 * server's offset. */
const dayBounds = (day: string) => {
  if (!day) return { date_from: undefined, date_to: undefined };
  const start = new Date(`${day}T00:00:00`);
  const end = new Date(`${day}T23:59:59.999`);
  return { date_from: start.toISOString(), date_to: end.toISOString() };
};

const Logs: React.FC = () => {
  const [logs, setLogs] = useState<ApiLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // `filters` is what the user is typing; `applied` is what the last request
  // used. Separating them keeps the table still while an IMSI is half typed.
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);

  const query = useCallback(() => ({
    page,
    size: 50,
    imsi: applied.imsi.trim() || undefined,
    country: applied.country || undefined,
    tadig: applied.tadig || undefined,
    ...dayBounds(applied.day),
  }), [page, applied]);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        const response = await apiService.getLogs(query());
        setLogs(response.items);
        setTotalPages(response.pages);
        setTotal(response.total);
      } catch (error) {
        console.error('Error fetching logs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [query]);

  useEffect(() => {
    apiService.getOperatorCountries().then(setCountries).catch(() => setCountries([]));
    apiService
      .getOperators({ size: 1000 })
      .then((r) => setOperators(r.items))
      .catch(() => setOperators([]));
  }, []);

  const search = () => {
    setPage(1);
    setApplied(filters);
  };

  const reset = () => {
    setPage(1);
    setFilters(EMPTY);
    setApplied(EMPTY);
  };

  // Narrowed to the chosen country, so the list stays usable at 531 operators.
  const operatorChoices = filters.country
    ? operators.filter((o) => o.country === filters.country)
    : operators;

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">API Logs</h1>
      <p className="mt-2 text-sm text-gray-700">
        View all steering API requests and responses.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div>
          <label htmlFor="imsi" className="block text-xs font-medium text-gray-700">IMSI</label>
          <input
            id="imsi"
            type="text"
            inputMode="numeric"
            value={filters.imsi}
            onChange={(e) => setFilters({ ...filters, imsi: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            placeholder="full or partial"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="day" className="block text-xs font-medium text-gray-700">Date</label>
          <input
            id="day"
            type="date"
            value={filters.day}
            onChange={(e) => setFilters({ ...filters, day: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="country" className="block text-xs font-medium text-gray-700">Country</label>
          <select
            id="country"
            value={filters.country}
            onChange={(e) => setFilters({ ...filters, country: e.target.value, tadig: '' })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          >
            <option value="">All countries</option>
            {countries.map((c) => (
              <option key={c.country} value={c.country}>{c.country}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="tadig" className="block text-xs font-medium text-gray-700">Operator</label>
          <select
            id="tadig"
            value={filters.tadig}
            onChange={(e) => setFilters({ ...filters, tadig: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          >
            <option value="">All operators</option>
            {operatorChoices.map((o) => (
              <option key={o.id} value={o.tadig_code}>
                {o.tadig_code} — {o.operator_name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={search}
            className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Search
          </button>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={() => apiService.exportLogs(query())}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            CSV
          </button>
        </div>
      </div>

      <p className="mt-3 text-sm text-gray-600">
        {loading ? 'Loading…' : `${total.toLocaleString()} matching requests`}
      </p>

      <div className="mt-6 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Timestamp
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Type
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      IMSI
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Queried Value
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Operator
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      TADIG
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Country
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Result
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Response Time
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4">
                        Loading...
                      </td>
                    </tr>
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4">
                        No logs found
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id}>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {log.request_type}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <span className="font-mono tabular-nums">{log.imsi}</span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {log.queried_value}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {log.matched_operator || '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm font-mono text-gray-500">
                          {log.matched_tadig || '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {log.matched_country || '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              log.is_unknown
                                ? 'bg-yellow-100 text-yellow-800'
                                : log.is_allowed
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {log.is_unknown ? 'Unknown' : log.is_allowed ? 'Allowed' : 'Denied'}
                          </span>
                          {/* A denial on a split country is otherwise
                              indistinguishable from a network that was never
                              permitted. */}
                          {log.steered_to_tadig && !log.is_allowed && (
                            <span className="ml-1 block text-xs text-gray-500">
                              steered to {log.steered_to_tadig}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {log.response_time_ms?.toFixed(2)}ms
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Logs;